/* ═══════════════════════════════════════════════════════════════
   WORKSPACE NAV — the one map of the tachograph workspace.

   Standalone draws it as the sidebar, embedded (inside DDIR) as the top
   bar; the sections, their order, their names and the role gates are
   the same in both (UX audit 2026-10-16, A1). Pure: no React, no
   window. The dashboard reports which record pages this organisation
   has (the Earned Recognition gate lives there); everything else is a
   role or feature flag from AuthContext.

   Item target: { view, tab? } — `view` is App's activeView, `tab` the
   dashboard tab when view is 'tachograph'.
   ═══════════════════════════════════════════════════════════════ */

/* Front-door screens: always first, always visible. */
export const FRONT_DOOR = [
  { key: 'today', label: 'Today', view: 'tachograph', tab: 'today' },
  { key: 'calendar', label: 'Calendar', view: 'tachograph', tab: 'calendar' },
  { key: 'people', label: 'People & vehicles', view: 'tachograph', tab: 'people' },
];

/* Records, grouped by the job a manager is doing (audit A3). A tab key
   the dashboard does not offer (an org outside Earned Recognition) is
   dropped; a key not listed here is never shown (Fleet command, 'overview',
   is retired from the map but still answers an old ?tab= link; its grids
   live on Today). Vehicles we've spotted, 'discoveries', moved to People &
   vehicles › Data with Data integrity (Zed, 2026-10-16). */
export const RECORD_GROUPS = [
  { key: 'worklists', label: 'Worklists', tabs: ['infringements', 'mileage', 'fraudAlerts'] },
  { key: 'behaviour', label: 'Driving behaviour', tabs: ['speeding', 'earnedRecognition'] },
  { key: 'data', label: 'Data', tabs: ['dataCentre'] },
];

export const RECORD_LABELS = {
  infringements: 'Infringements',
  mileage: 'Driven with no card',
  fraudAlerts: 'Needs explaining',
  speeding: 'Speed & events',
  earnedRecognition: 'Earned Recognition',
  dataCentre: 'Downloads & files',
};

const isTab = (t) => typeof t === 'string' && t.length > 0;

/**
 * The record groups this workspace shows, each `{ key, label, items }`,
 * items `{ key, label, view, tab }` (or `{ key:'analogue', view:'analogue' }`
 * for the chart reader, which is its own page). Empty groups are dropped.
 *
 * @param {string[]} availableTabs  record tab keys the dashboard offers
 * @param {{ analogue?: boolean }} [opts]  show Analogue charts under Data
 */
export function recordGroups(availableTabs, { analogue = false } = {}) {
  const offered = new Set((Array.isArray(availableTabs) ? availableTabs : []).filter(isTab));
  const groups = RECORD_GROUPS.map((g) => {
    const items = g.tabs
      .filter((t) => offered.has(t))
      .map((t) => ({ key: t, label: RECORD_LABELS[t], view: 'tachograph', tab: t }));
    if (g.key === 'data' && analogue) items.push({ key: 'analogue', label: 'Analogue charts', view: 'analogue' });
    return { key: g.key, label: g.label, items };
  });
  return groups.filter((g) => g.items.length > 0);
}

/**
 * The whole map: `[{ key, label, items?, groups? }]`. A section with
 * `target` is a single destination; one with `items` or `groups` is a menu.
 *
 * @param {object} args
 * @param {string[]} args.recordTabs   record tab keys the dashboard offers
 * @param {boolean} [args.isAdmin]
 * @param {boolean} [args.isSuperAdmin]
 * @param {boolean} [args.analogue]    the org has the chart reader (admin only)
 */
export function workspaceSections({ recordTabs, isAdmin = false, isSuperAdmin = false, analogue = false } = {}) {
  const sections = FRONT_DOOR.map((item) => ({ key: item.key, label: item.label, target: item }));
  // Before the dashboard has said which record pages it has, the menu
  // still opens on the worklists every organisation has.
  const tabs = Array.isArray(recordTabs) && recordTabs.length ? recordTabs : ['infringements', 'mileage', 'fraudAlerts', 'speeding', 'dataCentre'];
  sections.push({ key: 'records', label: 'Records', groups: recordGroups(tabs, { analogue: Boolean(isAdmin && analogue) }) });
  sections.push({
    key: 'reports',
    label: 'Reports',
    items: [
      { key: 'managementReport', label: 'Risk report', view: 'managementReport' },
      { key: 'reportSuite', label: 'Report suite', view: 'reportSuite' },
    ],
  });
  const settings = [];
  if (isAdmin) {
    settings.push({ key: 'orgSettings', label: 'Organisation', view: 'orgSettings' });
    settings.push({ key: 'userManagement', label: 'Users', view: 'userManagement' });
  }
  if (isSuperAdmin) settings.push({ key: 'superAdmin', label: 'System admin', view: 'superAdmin' });
  if (settings.length) sections.push({ key: 'settings', label: 'Settings', items: settings });
  return sections;
}

/** Every leaf item of a section, groups flattened. */
export function sectionItems(section) {
  if (!section) return [];
  if (section.target) return [section.target];
  if (Array.isArray(section.groups)) return section.groups.flatMap((g) => g.items);
  return Array.isArray(section.items) ? section.items : [];
}

/** True when `item` is the page on screen. */
export function isCurrent(item, activeView, activeTab) {
  if (!item) return false;
  if (item.tab) return activeView === 'tachograph' && activeTab === item.tab;
  return activeView === item.view;
}

/** The section that holds the page on screen, or null. */
export function currentSection(sections, activeView, activeTab) {
  return (sections || []).find((s) => sectionItems(s).some((i) => isCurrent(i, activeView, activeTab))) || null;
}
